import { isEventInRange } from "./eventUtils";

export type EventFilters = {
  distance: number | undefined;
  dateRange: string;
  customStart: string;
  customEnd: string;
  month: number | undefined;
  year: number | undefined;
  games: string[];
  eventTypes: string[];
  sortBy: string;
};

export type EventListItem = {
  id?: string;
  primaryId?: string;
  sourceIds?: string[];
  startDateTime?: string;
  endDateTime?: string;
  groupedStartDate?: string;
  groupedEndDate?: string;
  distance?: number | null;
  [key: string]: unknown;
};

type EventLocation = {
  lat?: number;
  lng?: number;
};

type BuildFilteredEventsInput = {
  groupedEvents?: EventListItem[];
  groupedSavedEvents?: EventListItem[];
  appliedFilters: EventFilters;
  location?: EventLocation | null;
  savedOnly: boolean;
};

export const createClearedEventFilters = (sortBy = "distance"): EventFilters => ({
  distance: undefined,
  dateRange: "all",
  customStart: "",
  customEnd: "",
  month: undefined,
  year: undefined,
  games: [],
  eventTypes: [],
  sortBy,
});

export const buildFilteredEvents = ({
  groupedEvents = [],
  groupedSavedEvents = [],
  appliedFilters,
  location,
  savedOnly,
}: BuildFilteredEventsInput): EventListItem[] => {
  const listSource = savedOnly ? groupedSavedEvents : groupedEvents;
  let list = [...listSource];

  const hasDistanceContext =
    (Number.isFinite(location?.lat) && Number.isFinite(location?.lng)) ||
    list.some((event) => Number.isFinite(event.distance));

  const maxDistance = appliedFilters.distance;
  if (!savedOnly && hasDistanceContext && maxDistance != null) {
    list = list.filter(
      (event) => Number.isFinite(event.distance) && Number(event.distance) <= maxDistance,
    );
  }

  if (!savedOnly) {
    list = list.filter((event) =>
      isEventInRange(event, appliedFilters.dateRange, {
        start: appliedFilters.customStart,
        end: appliedFilters.customEnd,
      }),
    );
  }

  if (appliedFilters.sortBy === "soonest") {
    list.sort(
      (a, b) =>
        new Date(String(a.startDateTime || "")).getTime() -
        new Date(String(b.startDateTime || "")).getTime(),
    );
  } else if (hasDistanceContext) {
    list.sort((a, b) => {
      const left = a.distance == null ? Number.MAX_SAFE_INTEGER : a.distance;
      const right = b.distance == null ? Number.MAX_SAFE_INTEGER : b.distance;
      return left - right;
    });
  }

  if (!savedOnly) {
    return list;
  }

  return list;
};
